import { useText } from "@/lib/content";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, errorMessage, fieldErrors } from "@/lib/api";
import { Notice } from "@/components/form/FormFeedback";
import { Loading } from "@/components/layout/RequireAuth";
import { usePageTitle } from "@/components/layout/AppLayout";
import { AuthCard, TextInput } from "@/pages/Login";
import { MSG } from "@/lib/validators";
import { policyError } from "@/lib/passwordPolicy";
import { PasswordStrength } from "@/components/form/PasswordStrength";

const InvalidLink = () => (
    <div className="space-y-4">
        <Notice kind="error" role="alert" testId="reset-invalid-message">Link non valido o scaduto</Notice>
        <Link to="/password-dimenticata" className="btn-primary" data-testid="reset-request-new-link">Richiedi un nuovo link</Link>
    </div>
);

export default function ResetPassword({ invite = false }) {
    const t = useText();
    usePageTitle(invite ? "Imposta la password" : "Reimposta la password");
    const [doneMsg, setDoneMsg] = useState("");
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [state, setState] = useState("checking");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!token) return setState("invalid");
        api.post("/auth/reset-password/check", { token, kind: invite ? "invite" : "reset" }).then(() => setState("form")).catch(() => setState("invalid"));
        return undefined;
    }, [token, invite]);

    const onSubmit = async (e) => {
        e.preventDefault();
        const errs = {};
        if (!password) errs.password = MSG.required;
        else if (policyError(password)) errs.password = policyError(password);
        if (!confirm) errs.password_confirm = MSG.required;
        else if (confirm !== password) errs.password_confirm = MSG.passwordMatch;
        setErrors(errs);
        if (Object.keys(errs).length) return;
        setBusy(true);
        try {
            const { data } = await api.post(invite ? "/auth/accept-invite" : "/auth/reset-password", { token, password, password_confirm: confirm });
            setDoneMsg(data.message);
            setState("done");
        } catch (err) {
            const fe = fieldErrors(err);
            if (fe) setErrors(fe);
            else if (errorMessage(err) === "Link non valido o scaduto") setState("invalid");
            else setErrors({ password: errorMessage(err) });
        } finally {
            setBusy(false);
        }
    };

    if (state === "checking") return <Loading />;
    return (
        <AuthCard title={t(invite ? "auth.set_title" : "auth.reset_title")}>
            {state === "invalid" && (invite ? <Notice kind="error" role="alert" testId="reset-invalid-message">Link non valido o scaduto</Notice> : <InvalidLink />)}
            {state === "done" && (
                <div className="space-y-4">
                    <Notice kind="success" testId="reset-success-message">{doneMsg}</Notice>
                    <Link to="/accedi" className="btn-primary" data-testid="reset-go-login-link">Accedi</Link>
                </div>
            )}
            {state === "form" && (
                <form noValidate onSubmit={onSubmit} className="space-y-5" data-testid="reset-form">
                    <div>
                        <TextInput id="reset-password" label="Nuova password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} error={errors.password}
                            aria-describedby={`reset-password-strength${errors.password ? " reset-password-error" : ""}`} />
                        <PasswordStrength id="reset-password-strength" password={password} />
                    </div>
                    <TextInput id="reset-password-confirm" label="Conferma password" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} error={errors.password_confirm} />
                    <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="reset-submit-button">Salva la nuova password</button>
                </form>
            )}
        </AuthCard>
    );
}
